import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  EventAvailable,
  AccessTime
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay
} from 'date-fns';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

/* ================= STATUS CONFIG ================= */
const STATUS = {
  P: { label: 'Present', color: '#15803d', bg: '#ecfdf5' },
  A: { label: 'Absent', color: '#b91c1c', bg: '#fef2f2' },
  L: { label: 'Leave', color: '#7e22ce', bg: '#faf5ff' },
  W: { label: 'Week Off', color: '#c2410c', bg: '#fffbeb' },
  H: { label: 'Holiday', color: '#1d4ed8', bg: '#eef2ff' },
  PH: { label: 'Paid Holiday', color: '#1e40af', bg: '#e0e7ff' },
  WOS: { label: 'Work On Sunday', color: '#059669', bg: '#d1fae5' }
};

const EmployeeCalendar = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [attendance, setAttendance] = useState([]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!user?.employeeId) return;
    api
      .get(`/attendance/employee/${user.employeeId}`)
      .then(res => setAttendance(res.data || []))
      .catch(() => setAttendance([]));
  }, [month, user?.employeeId]);

  const findRecord = date =>
    attendance.find(a => a.date === format(date, 'yyyy-MM-dd'));

  /* ================= DATE RANGE (6 FULL WEEKS) ================= */
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  return (
    <Box sx={{ p: 4, bgcolor: '#76899cff', minHeight: '100vh' }}>
      <Paper sx={{ p: 4, borderRadius: 4 }}>

        {/* ================= HEADER ================= */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventAvailable color="primary" />
            <Typography variant="h5" fontWeight={800}>
              My Attendance Calendar
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center">
            <IconButton onClick={() => setMonth(subMonths(month, 1))}>
              <ChevronLeft />
            </IconButton>
            <Typography fontWeight={700} sx={{ minWidth: 160, textAlign: 'center' }}>
              {format(month, 'MMMM yyyy')}
            </Typography>
            <IconButton onClick={() => setMonth(addMonths(month, 1))}>
              <ChevronRight />
            </IconButton>
          </Stack>
        </Stack>

        {/* ================= DAY NAMES ================= */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            mb: 1
          }}
        >
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
            <Typography
              key={d}
              align="center"
              fontWeight={700}
              fontSize={12}
              color={d === 'SUN' ? 'error.main' : 'text.secondary'}
            >
              {d}
            </Typography>
          ))}
        </Box>

        {/* ================= CALENDAR GRID ================= */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gridAutoRows: '140px',
            border: '1px solid #5280deff',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          {days.map(d => {
            const record = findRecord(d);
            const isSunday = d.getDay() === 0;
            const isCurrentMonth = isSameMonth(d, month);

            let code = record?.displayStatus;
            if (!code && isSunday) code = 'W';

            const status = STATUS[code];

            return (
              <Box
                key={d.toString()}
                sx={{
                  borderRight: '1px solid #e5e7eb',
                  borderBottom: '1px solid #e5e7eb',
                  p: 1.5,
                  bgcolor: !isCurrentMonth
                    ? '#f1f5f9'
                    : status?.bg || '#ffffff'
                }}
              >
                <Typography
                  fontSize={12}
                  fontWeight={isSameDay(d, new Date()) ? 900 : 600}
                  color={!isCurrentMonth ? 'text.disabled' : 'text.primary'}
                >
                  {format(d, 'd')}
                </Typography>

                {status && isCurrentMonth && (
                  <Tooltip
                    title={
                      record
                        ? `In: ${record.clockInTime || '-'} | Out: ${record.clockOutTime || '-'}`
                        : status.label
                    }
                  >
                    <Stack spacing={0.5} mt={1}>
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{
                          bgcolor: status.color,
                          color: '#fff',
                          fontWeight: 700
                        }}
                      />
                      {record?.clockInTime && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <AccessTime sx={{ fontSize: 12 }} />
                          <Typography fontSize={11}>
                            {record.clockInTime.substring(0, 5)}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ================= LEGEND ================= */}
        <Stack direction="row" spacing={2} justifyContent="center" mt={3} flexWrap="wrap">
          <Chip label="Present" sx={{ bgcolor: '#15803d', color: '#fff' }} />
          <Chip label="Absent" sx={{ bgcolor: '#b91c1c', color: '#fff' }} />
          <Chip label="Leave" sx={{ bgcolor: '#7e22ce', color: '#fff' }} />
          <Chip label="Week Off" sx={{ bgcolor: '#c2410c', color: '#fff' }} />
          <Chip label="Holiday" sx={{ bgcolor: '#1d4ed8', color: '#fff' }} />
        </Stack>

      </Paper>
    </Box>
  );
};

export default EmployeeCalendar;
